import { useRef, useState, useCallback } from "react";

// Components
import ContentContainer from "../../../layouts/ContentContainer/ContentContainer";
import NavBar from "../../../components/NavBar/NavBar";
import Carousel from "../../../components/Carousel/Carousel";
import EmbedVideo from "../../../components/VideoContainer/EmbedVideo";

// Data for a widget with large buttons
import largeButtonsData from "./largeButtonsData";
// Buttons
import LargeButton from "../../../components/Buttons/LargeButton";
import ScrollToTopButton from "../../../components/Buttons/ScrollToTopButton";
// Assets
import hermonImage from "../../../assets/imgs/culture-page/hermon.png";
import cornerDecor from "../../../assets/imgs/culture-page/corner-decor.svg";
import familyImg from "../../../assets/imgs/culture-page/_Family.svg";
// Custom hook
import { useMediaQuery } from "../../../custom-hooks/useMediaQuery";

const Culture = () => {
   const desktopView = useMediaQuery("(min-width: 768px)");
   const pageRef = useRef(null);

   /* Carousel Controls - start */

   const [isPopupVisible, setIsPopupVisible] = useState(false);
   const [carouselPage, setCarouselPage] = useState(-1);

   const togglePopup = useCallback((num) => {
      if (num < 0) {
         setIsPopupVisible(false);
      } else {
         setCarouselPage(num);
         setIsPopupVisible(true);
      }
   }, []);

   /* Carousel Controls - end */

   const largeButtonsList = largeButtonsData.map((item, index) => (
      <li key={item.title}>
         <LargeButton
            title={item.title}
            image={item.img}
            onClick={() => {
               togglePopup(index + 1);
            }}
         />
      </li>
   ));
   return (
      <div>
         <div
            ref={pageRef}
            className="page page-book base-page-culture page--font-dark"
         >
            <div className="page__inner">
               <header id="page-three-top" className="header">
                  <div className="background-image--culture"></div>
                  <NavBar />
                  <div className="hero">
                     <div className="hero__container">
                        <div className="hero__text">
                           <p className="hero__paragraph">
                              Great culture. The people really make it a great
                              place to work. One of the best decisions I have
                              made coming to work at Avado.
                           </p>
                        </div>
                     </div>
                  </div>
               </header>
               <main>
                  <ContentContainer>
                     <div className="text-container text-container--2col">
                        <h1 className="heading heading--primary">
                           Our Culture
                        </h1>
                        <p>
                           Culture is often difficult to describe and distil.
                           But hopefully, we will do an increasingly better job
                           of it in Avado Life. Like our business, our culture
                           is always evolving with each and every new person who
                           joins us. We welcome this. And to help you get
                           started, we’re going to explore our culture as it is
                           today with you, in the following terms:
                        </p>
                        <ul className="paragraph paragraph__list paragraph__list--highlight">
                           <li>Decision-making;</li>
                           <li>Meetings and connections;</li>
                           <li>Norms and rituals;</li>
                           <li>Priorities, purpose, values and behaviours;</li>
                           <li>Rituals;</li>
                           <li>Feedback;</li>
                           <li>Psychological safety.</li>
                        </ul>
                        <p>
                           We believe these areas are core to the experience of
                           our special culture, and breaking it down in this way
                           aims to help us all articulate our culture in a
                           directional, meaningful, tangible way. But first
                           things first:{" "}
                           <strong className="paragraph--highlight">
                              humanity and values.
                           </strong>
                        </p>
                        <img
                           src={familyImg}
                           alt=""
                           className="img-responsive"
                        />
                     </div>
                  </ContentContainer>

                  <ContentContainer>
                     <h2 className="heading heading--secondary">
                        A bit about our humanity
                     </h2>
                     <p>
                        There is no better a way to bring our humanity to life
                        than to invite you to experience it every day, in your
                        interactions with Avado people. But as we’ve committed
                        to making this directional, meaningful and tangible,
                        here’s a little description of it for you to ponder.
                     </p>
                     <p className="heading heading--quaternary">
                        Our basic philosophy is that “Hard” is “Soft” and “Soft”
                        is “Hard”. Try this for size:
                     </p>
                     <ul className="paragraph paragraph__list text-container text-container--2col">
                        <li>
                           Hard facts (numbers / plans / org charts) are
                           actually soft, because:
                        </li>
                        <ul className="paragraph paragraph__list">
                           <li>Plans are fantasies; </li>
                           <li>
                              Organisational charts often have little to do with
                              the way our organisation actually works;
                           </li>
                           <li>
                              Numbers could be readily manipulated by
                              smart-enough people (which we really don’t
                              encourage).
                           </li>
                        </ul>
                        <li>
                           We therefore focus on best-ever people practices,
                           such as caring, learning, recognising, celebrating,
                           rewarding and including. Why? Because we strongly
                           believe that these practices create the most
                           wholesome, community-minded organisations, and win in
                           the marketplace as well. We also believe that these
                           things are all by-products of a supportive culture,
                           nurtured one day at a time by really special people:
                        </li>
                        <ul className="paragraph paragraph__list">
                           <li>Effective people practices;</li>
                           <li>Product design that inspires;</li>
                           <li>Clients who are enthralled;</li>
                           <li>
                              Partners and suppliers who bend over backwards to
                              assist us.
                           </li>
                        </ul>
                        <p className="heading heading--quaternary">
                           Think: What do you take away from this?
                        </p>
                     </ul>
                  </ContentContainer>
                  <ContentContainer
                     modifier="image"
                     styleClass="content-container__container--2col"
                  >
                     <div className="text-container text-container--1col text-container--white-bg">
                        <h3 className="heading heading--tertiary">
                           Underneath all of this are our values...
                        </h3>
                        <p>
                           Our values have proven to be core to what drives our
                           Purpose, our decisions and our interactions with
                           others, and in so doing we have created a
                           human-centred working environment.
                        </p>
                     </div>
                     {desktopView && (
                        <div className="image-container">
                           <img src={hermonImage} alt="" className="" />
                        </div>
                     )}
                  </ContentContainer>
                  <ContentContainer>
                     <h2 className="heading heading--secondary">
                        Our values Manifesto
                     </h2>
                     <div className="text-container text-container--2col">
                        <ul className="paragraph paragraph__list">
                           <li>
                              We truly respect that each person will have their
                              own personal way of interpreting what our values
                              mean to them and apply them accordingly to their
                              thought processes, decisions, actions and/or
                              behaviours.
                           </li>
                        </ul>
                        <ul className="paragraph paragraph__list">
                           <li>
                              We don’t believe in culture fit; we believe in
                              culture contribution - everyone’s individuality
                              should be celebrated, and not condensed to fill a
                              ‘one-size-fits-all’ mould.
                           </li>
                        </ul>
                        <ul className="paragraph paragraph__list">
                           <li>
                              We don’t call people “staff” or “employees”; we
                              call people, “people” or “person”. It’s just a bit
                              more human, isn’t it.
                           </li>
                        </ul>
                     </div>
                  </ContentContainer>
                  <ContentContainer>
                     <h2 className="heading heading--secondary">
                        A sense of purpose
                     </h2>
                     <div className="text-container text-container--2col">
                        <p>
                           Being a learning provider, it's natural that our
                           culture embodies the spirit of growth, teamwork, and
                           innovation. Collectively we strive to create
                           incredible experiences and products for our people,
                           customers and partners.
                        </p>
                        <p>
                           We are not perfect! There is always room to improve,
                           explore new ideas and take risks in our pursuit to
                           surpass remarkableness and excellence. In fact, this
                           is crucial to stay ahead of the curve and remain a
                           key player in the marketplace.
                        </p>
                        <p>
                           Sharing our expertise via young-adult mentoring
                           schemes and our FastFutures programme brings the joy
                           of teaching to our work schedules – we embrace
                           variety and are proud to incorporate fun and personal
                           development into our mission.
                        </p>
                        <p className="heading heading--quaternary">
                           Above all, we aim to use our values to produce and
                           deliver incredible learning experiences that create
                           real change in peoples' lives.
                        </p>
                     </div>
                  </ContentContainer>
                  <ContentContainer modifier="gradient">
                     <div className="text-container text-container--white-bg text-container--img-corner">
                        <h2 className="heading heading--secondary">
                           And the values themselves...
                        </h2>
                        <p>
                           <b>Short story</b>: We co-created our values – that
                           means we designed them with our people, for our
                           people. (There’s a longer version of this story,
                           which you’ll hear about in your on-boarding process.)
                        </p>
                        <p>
                           <b>Longer short story</b>: We believe that for people
                           to come and be free to do their best work, there
                           needs to be a collectively-agreed set of beliefs and
                           behaviours that keep us all aligned. Why? Because the
                           best organisations in the world do this, and it’s
                           been proven to be effective in multiple human
                           scientific studies.
                        </p>
                        <p className="heading heading--quaternary">
                           So what are our values, and how do they work?
                        </p>
                        <p>
                           When we co-created our values, we wanted values that
                           would:
                        </p>
                        <ul className="paragraph paragraph__list text-container text-container--2col">
                           <li>
                              Guide all of our decisions and ways of working;
                           </li>
                           <li>Underline everything we do as a company;</li>
                           <li>Guide how we treat people;</li>
                           <li>Facilitate our behaviour;</li>
                           <li>Guide hiring decisions;</li>
                           <li>
                              Stimulate fun and innovation, and nurture a
                              culture of success;
                           </li>
                           <li>Articulate our identity.</li>
                        </ul>
                        <h3 className="heading heading--tertiary">
                           Guiding Principles
                        </h3>
                        <p>
                           And to keep our approach simple, we agreed some
                           Guiding Principles, which helped us avoid prescribing
                           detailed interpretations of each word (if you
                           prescribe like that, we believe you get robots, and
                           frankly, we prefer people.)
                        </p>
                        <p className="heading heading--quaternary">
                           Here’s where we landed with our Guiding Principles:
                        </p>
                        <div className="text-container text-container--2col">
                           <ul className="paragraph paragraph__list">
                              <li>
                                 We see our values as intellectual property that
                                 differentiates us from our competitors and
                                 can’t be easily replicated;
                              </li>
                              <li>
                                 Our values describe our act, not just think, to
                                 deliver our goals: We won’t describe every
                                 behaviour, just those we see as non-negotiable;
                              </li>
                              <li>
                                 They will guide and influence our decisions,
                                 and we will take time to apply our values
                                 up-front, rather than post-rationalise;
                              </li>
                              <li>
                                 Our values energise and enable all of us to
                                 clearly contribute, or even double-down on
                                 efforts, to continually evolve our culture, and
                                 positively impact our people’s, clients’ and
                                 leaners’ happiness;
                              </li>
                              <li>They will have measurable outcomes;</li>
                              <li>
                                 And they are there to support our leaders in
                                 managing the ways we work together, acting as a
                                 ‘north star’ for performance.
                              </li>
                           </ul>
                           <div>
                              <p>
                                 Going back to what was said earlier about how
                                 we’ll describe the culture well, we’ll break
                                 our values down into the “culture categories”:
                              </p>
                              <ul className="paragraph paragraph__list">
                                 <li>Decision-making;</li>
                                 <li>Meetings and connections;</li>
                                 <li>Norms and rituals;</li>
                                 <li>
                                    Priorities, purpose, values and behaviours;
                                 </li>
                                 <li>Rituals;</li>
                                 <li>Feedback;</li>
                                 <li>Psychological safety.</li>
                              </ul>
                              <p className="heading heading--quaternary">
                                 Click on each value in the section below to
                                 find out more about this list against each of
                                 our values.
                              </p>
                           </div>
                        </div>
                        <div className="corner-image">
                           <img src={cornerDecor} alt="" />
                        </div>
                     </div>
                  </ContentContainer>
                  <ContentContainer>
                     <h2 className="heading heading--secondary heading--center">
                        Click each hotspot to find out more about our values
                     </h2>
                     <ul className="large-buttons-container">
                        {largeButtonsList}
                     </ul>
                  </ContentContainer>
                  <ContentContainer>
                     <h2 className="heading heading--secondary">
                        You’re free to do your best work – but what does this
                        really mean?
                     </h2>
                     <p>
                        We work hard to create a working environment that is
                        safe, collaborative, innovative, creative, and empowered
                        – allowing you to use your own approach to positively
                        impact all that Avado is and does.
                     </p>
                     <p className="heading heading--quaternary">
                        In practice, there are a few things you can look forward
                        to here:
                     </p>
                     <div className="text-container text-container--2col">
                        <ul className="paragraph paragraph__list">
                           <li>
                              Open questions – we believe everyone should be
                              able to contribute to the discussion of “company
                              policy” and news. Questions can be submitted
                              anonymously to our company-wide meetings – so if
                              there’s something you want to know, just ask!
                              Likewise, our engagement platform – OfficeVibe –
                              is a good place to share your feedback
                           </li>
                        </ul>
                        <ul className="paragraph paragraph__list">
                           <li>
                              Innovation – if you have a new idea, don't
                              hesitate! We all want to hear it. Reach out to
                              colleagues for expert advice, refine it, apply it
                              and share with everyone how it makes a positive
                              difference
                           </li>
                        </ul>

                        <ul className="paragraph paragraph__list">
                           <li>
                              Our work times are flexible – we don’t watch
                              clocks (unless it’s to be on time for clients or
                              learners). Each team (and individual!) manages
                              their own time proactively and effectively,
                              fitting around clients’, learners’ and other
                              stakeholders' needs
                           </li>
                        </ul>
                        <ul className="paragraph paragraph__list">
                           <li>
                              Transparency – if you want to know something, just
                              ask. There will be times where we may not be able
                              to share everything we’d like to, and there will
                              be good reasons for that – but we aim to be100%
                              open as often as possible
                           </li>
                        </ul>
                     </div>
                  </ContentContainer>

                  <ContentContainer>
                     <ScrollToTopButton parentContainer={pageRef} />
                  </ContentContainer>
               </main>
            </div>
         </div>

         {isPopupVisible && (
            <Carousel carouselPage={carouselPage} togglePopup={togglePopup} />
         )}
      </div>
   );
};

export default Culture;
